import { Image } from '~/elements/Image/Image'
import { Filter } from '~/modules/PLP/components/Filter'
import { parseFilterString } from '~/modules/PLP/lib/parseFilterString'
import { Button } from '~/elements/Button/Button'
import { Aggregation } from '@magentoTypes'
import { useState } from 'react'
import clsx from 'clsx'
import { useMedia } from 'hooks/useMedia'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { ButtonMain } from '~/elements/Button/ButtonMain'

type CioFiltersProps = {
  filters?: Aggregation[]
  handleFilterToggle: (isOpen: boolean) => void
  handleFilterChange: (filter: string) => void
  handleOpenExactFilter: (exactFilterName?: string) => void
  showFilters: boolean
  initiallyOpenedFilter?: number
}

export const CioFilters = ({
  filters,
  handleFilterToggle,
  showFilters,
  handleFilterChange,
  handleOpenExactFilter,
  initiallyOpenedFilter,
}: CioFiltersProps) => {
  const router = useRouter()
  const [internalFilter, setInternalFilter] = useState((router.query.filter as string) ?? '')
  const isDesktop = useMedia('lg')

  const { t } = useTranslation()

  const filterList =
    filters?.filter(
      (ag) =>
        ag?.attribute_code !== 'price' &&
        ag?.attribute_code !== 'category_id' &&
        ag?.attribute_code !== 'category_uid',
    ) || []

  const handleResetFilters = async () => {
    delete router.query.filter,
      await router.push({
        pathname: router.pathname,
        query: {
          ...router.query,
          page: 1,
        },
      })
    handleFilterToggle(false)
    handleOpenExactFilter(undefined)
    setInternalFilter('')
  }

  const handleSetFilter = (filter: string) => {
    setInternalFilter(filter)
  }

  const filterAnimation = isDesktop
    ? showFilters
      ? 'translate-x-0 opacity-1'
      : 'translate-x-[550px] opacity-0'
    : showFilters
    ? 'translate-y-0 opacity-1'
    : 'translate-y-[-100vh] opacity-0'

  return (
    <>
      <div
        className={clsx(
          'fixed top-0 right-0 z-[60] h-screen w-full bg-sg-white transition-all duration-300 lg:w-[550px]',
          filterAnimation,
        )}
      >
        <div className={clsx('vh100 relative w-full lg:flex lg:justify-end')}>
          <div className="flex w-full flex-col">
            <button
              id="sidebar-close-button"
              className="absolute top-4 right-4 z-[60] grid h-10 w-10 place-content-center rounded-full bg-grey-light text-grey-dark lg:top-8 lg:right-8"
              onClick={() => {
                handleFilterToggle(false)
                handleOpenExactFilter()
              }}
            >
              <Image
                src="/images/Icons/close.svg"
                width={24}
                height={24}
                placeholder="empty"
                alt="close-icon"
              />
            </button>
            <header
              className={clsx('flex items-center justify-between p-4 font-medium lg:p-10')}
              style={{ boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.15)' }}
            >
              <span className="text-h4-mobile lg:text-h3-mobile">{t('Filter')}</span>
            </header>
            <div
              className={clsx(
                'flex h-full max-h-[100vh] flex-1 flex-col overflow-auto p-4 pb-40 md:p-12 md:pb-44',
                {
                  'overflow-y-auto': !isDesktop,
                },
              )}
            >
              {showFilters && (
                <Filter
                  aggregations={filterList as Aggregation[]}
                  initialOpen={initiallyOpenedFilter}
                  filter={internalFilter}
                  setFilter={handleSetFilter}
                />
              )}
            </div>
            <div
              className="absolute bottom-0 w-full p-4 md:p-12"
              style={{
                background:
                  'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 40%)',
              }}
            >
              {Object.keys(parseFilterString(router.query.filter as string)).length > 0 ? (
                <Button
                  ctaButtonStyle="MediumText"
                  onClick={handleResetFilters}
                  className="z-50 mb-5 w-full"
                >
                  {t('Reset Filters')}
                </Button>
              ) : null}
              <ButtonMain
                className="mt-auto"
                fullWidth
                onClick={() => handleFilterChange(internalFilter)}
              >
                {t('Confirm Selection')}
              </ButtonMain>
            </div>
          </div>
        </div>
      </div>
      <div
        onClick={() => {
          handleFilterToggle(false)
          handleOpenExactFilter()
        }}
        className={clsx(
          'fixed top-0 left-0 z-50 hidden h-full w-full bg-sg-black/[0.6] lg:block',
          !showFilters && 'lg:hidden',
        )}
      />
    </>
  )
}
